import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Building2, Plus, Pencil, Trash2, Upload, X, Check, AlertCircle, ExternalLink } from 'lucide-react';
import { useSelector } from 'react-redux';
import { authSelector } from '../features/auth/slice';

const THEME_OPTIONS = [
  { value: 'dark', label: '다크모드' },
  { value: 'light', label: '라이트모드' },
];

function DistributorModal({ dist, onClose, onSaved }) {
  const isNew = !dist;
  const [form, setForm] = useState({
    name: dist?.name || '',
    slug: dist?.slug || '',
    theme: dist?.theme || 'light',
    contactEmail: dist?.contactEmail || '',
    contactPhone: dist?.contactPhone || '',
    businessNumber: dist?.businessNumber || '',
    representative: dist?.representative || '',
    address: dist?.address || '',
    memo: dist?.memo || '',
    status: dist?.status || 'active',
    adminLoginId: '',
    adminPassword: '',
  });
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(dist?.logo || null);
  const [logoDeleted, setLogoDeleted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const fileRef = useRef();

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      let saved;
      if (dist) {
        const { data } = await axios.put(`/api/distributors/${dist.id}`, form);
        saved = data;
      } else {
        const { data } = await axios.post('/api/distributors', form);
        saved = data;
      }

      // 로고 삭제
      if (logoDeleted && !logoFile) {
        await axios.delete(`/api/distributors/${saved.id}/logo`);
        saved.logo = null;
      }
      // 로고 업로드
      if (logoFile) {
        const fd = new FormData();
        fd.append('logo', logoFile);
        const { data } = await axios.post(`/api/distributors/${saved.id}/logo`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        saved.logo = data.logo;
      }

      onSaved(saved);
    } catch (err) {
      setError(err.response?.data?.message || '저장 실패');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center pt-4 px-4 pb-24">
      <div className="bg-[#182334] rounded-2xl w-full max-w-md border border-white/[0.08] shadow-2xl flex flex-col max-h-[calc(100vh-8rem)]">
        <div className="flex items-center justify-between px-6 pt-6 pb-4 border-b border-white/[0.08] shrink-0">
          <h2 className="text-white font-bold text-lg">{dist ? '유통사 수정' : '유통사 추가'}</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-white transition-colors"><X size={20} /></button>
        </div>
        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
          <div className="px-6 py-5 space-y-4 overflow-y-auto flex-1 sidebar-scroll">
          {error && (
            <div className="flex items-center gap-2 text-rose-400 text-sm bg-rose-500/10 rounded-xl px-3 py-2">
              <AlertCircle size={15} /> {error}
            </div>
          )}

          {/* 로고 */}
          <div className="flex items-center gap-4">
            <div
              className="w-16 h-16 rounded-xl border border-white/[0.08] bg-white/5 flex items-center justify-center overflow-hidden cursor-pointer"
              onClick={() => fileRef.current?.click()}>
              {logoPreview
                ? <img src={logoPreview} alt="logo" className="w-full h-full object-contain" />
                : <Upload size={20} className="text-slate-500" />}
            </div>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleFileChange} />
            <div>
              <button type="button" onClick={() => fileRef.current?.click()} className="text-sky-400 text-sm hover:underline">로고 업로드</button>
              {logoPreview && (
                <button type="button" onClick={() => { setLogoPreview(null); setLogoFile(null); setLogoDeleted(true); }}
                  className="ml-3 text-rose-400 text-sm hover:underline">삭제</button>
              )}
              <p className="text-slate-500 text-xs mt-0.5">PNG, SVG 권장 · 최대 5MB</p>
            </div>
          </div>

          <div className="space-y-3">
            <div>
              <label className="text-xs font-semibold text-slate-400 uppercase tracking-wide block mb-1">회사명 *</label>
              <input className="w-full px-3 py-2 rounded-xl border border-white/[0.08] bg-[#1a2b42] text-sm text-slate-200 placeholder-slate-500 outline-none focus:ring-2 focus:ring-sky-500/30"
                value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="예) 뮤직코리아" required />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-400 uppercase tracking-wide block mb-1">Slug * <span className="normal-case text-slate-500">(영문, 하이픈만)</span></label>
              <input className="w-full px-3 py-2 rounded-xl border border-white/[0.08] bg-[#1a2b42] text-sm text-slate-200 placeholder-slate-500 outline-none focus:ring-2 focus:ring-sky-500/30"
                value={form.slug} onChange={e => setForm(f => ({ ...f, slug: e.target.value }))} placeholder="예) music-korea" required
                disabled={dist?.id === 1} />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-400 uppercase tracking-wide block mb-1">테마</label>
              <select className="w-full px-3 py-2 rounded-xl border border-white/[0.08] bg-[#1a2b42] text-sm text-slate-200 outline-none focus:ring-2 focus:ring-sky-500/30"
                value={form.theme} onChange={e => setForm(f => ({ ...f, theme: e.target.value }))}>
                {THEME_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-400 uppercase tracking-wide block mb-1">담당자 이메일</label>
              <input type="email" className="w-full px-3 py-2 rounded-xl border border-white/[0.08] bg-[#1a2b42] text-sm text-slate-200 placeholder-slate-500 outline-none focus:ring-2 focus:ring-sky-500/30"
                value={form.contactEmail} onChange={e => setForm(f => ({ ...f, contactEmail: e.target.value }))} placeholder="contact@company.com" />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-400 uppercase tracking-wide block mb-1">담당자 전화번호</label>
              <input type="tel" className="w-full px-3 py-2 rounded-xl border border-white/[0.08] bg-[#1a2b42] text-sm text-slate-200 placeholder-slate-500 outline-none focus:ring-2 focus:ring-sky-500/30"
                value={form.contactPhone} onChange={e => setForm(f => ({ ...f, contactPhone: e.target.value }))} placeholder="010-0000-0000" />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-400 uppercase tracking-wide block mb-1">대표자명 <span className="normal-case text-slate-500">(푸터 표시)</span></label>
              <input className="w-full px-3 py-2 rounded-xl border border-white/[0.08] bg-[#1a2b42] text-sm text-slate-200 placeholder-slate-500 outline-none focus:ring-2 focus:ring-sky-500/30"
                value={form.representative} onChange={e => setForm(f => ({ ...f, representative: e.target.value }))} placeholder="홍길동" />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-400 uppercase tracking-wide block mb-1">사업자등록번호 <span className="normal-case text-slate-500">(푸터 표시)</span></label>
              <input className="w-full px-3 py-2 rounded-xl border border-white/[0.08] bg-[#1a2b42] text-sm text-slate-200 placeholder-slate-500 outline-none focus:ring-2 focus:ring-sky-500/30"
                value={form.businessNumber} onChange={e => setForm(f => ({ ...f, businessNumber: e.target.value }))} placeholder="000-00-00000" />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-400 uppercase tracking-wide block mb-1">주소 <span className="normal-case text-slate-500">(푸터 표시)</span></label>
              <input className="w-full px-3 py-2 rounded-xl border border-white/[0.08] bg-[#1a2b42] text-sm text-slate-200 placeholder-slate-500 outline-none focus:ring-2 focus:ring-sky-500/30"
                value={form.address} onChange={e => setForm(f => ({ ...f, address: e.target.value }))} placeholder="서울시 강남구 ..." />
            </div>
            {dist && (
              <div>
                <label className="text-xs font-semibold text-slate-400 uppercase tracking-wide block mb-1">상태</label>
                <select className="w-full px-3 py-2 rounded-xl border border-white/[0.08] bg-[#1a2b42] text-sm text-slate-200 outline-none focus:ring-2 focus:ring-sky-500/30"
                  value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}>
                  <option value="active">운영중</option>
                  <option value="inactive">중단</option>
                </select>
              </div>
            )}
            <div>
              <label className="text-xs font-semibold text-slate-400 uppercase tracking-wide block mb-1">메모</label>
              <textarea rows={2} className="w-full px-3 py-2 rounded-xl border border-white/[0.08] bg-[#1a2b42] text-sm text-slate-200 placeholder-slate-500 outline-none focus:ring-2 focus:ring-sky-500/30 resize-none"
                value={form.memo} onChange={e => setForm(f => ({ ...f, memo: e.target.value }))} placeholder="내부 메모" />
            </div>

            {isNew && (
              <>
                <div className="border-t border-white/[0.08] pt-4">
                  <p className="text-xs font-bold text-slate-300 mb-3">관리자 계정 *</p>
                  <div className="space-y-3">
                    <div>
                      <label className="text-xs font-semibold text-slate-400 uppercase tracking-wide block mb-1">아이디 *</label>
                      <input className="w-full px-3 py-2 rounded-xl border border-white/[0.08] bg-[#1a2b42] text-sm text-slate-200 placeholder-slate-500 outline-none focus:ring-2 focus:ring-sky-500/30"
                        value={form.adminLoginId} onChange={e => setForm(f => ({ ...f, adminLoginId: e.target.value }))} placeholder="admin_musickorea" required />
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-slate-400 uppercase tracking-wide block mb-1">비밀번호 *</label>
                      <input type="password" className="w-full px-3 py-2 rounded-xl border border-white/[0.08] bg-[#1a2b42] text-sm text-slate-200 placeholder-slate-500 outline-none focus:ring-2 focus:ring-sky-500/30"
                        value={form.adminPassword} onChange={e => setForm(f => ({ ...f, adminPassword: e.target.value }))} placeholder="초기 비밀번호" required />
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>

          </div>

          <div className="flex gap-2 px-6 py-4 border-t border-white/[0.08] shrink-0">
            <button type="button" onClick={onClose} className="flex-1 px-4 py-2 rounded-xl text-sm font-semibold bg-white/10 hover:bg-white/15 text-slate-300 transition-colors">취소</button>
            <button type="submit" disabled={saving}
              className="flex-1 px-4 py-2 rounded-xl text-sm font-semibold bg-sky-500/20 hover:bg-sky-500/30 text-sky-400 transition-colors flex items-center justify-center gap-2">
              {saving ? '저장 중...' : <><Check size={15} /> 저장</>}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function DistributorsPage() {
  const { me } = useSelector(authSelector.selectAuthState);
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState('');
  const [modal, setModal] = useState(null); // null | 'create' | dist object
  const [deleteTarget, setDeleteTarget] = useState(null);

  useEffect(() => {
    fetchList();
  }, []);

  const fetchList = async () => {
    try {
      const { data } = await axios.get('/api/distributors');
      setList(data);
    } catch (err) {
      const msg = err.response?.data?.message || err.message || '알 수 없는 오류';
      setFetchError(`[${err.response?.status || 'ERR'}] ${msg}`);
      console.error('유통사 목록 오류:', err);
    }
    setLoading(false);
  };

  const handleSaved = (saved) => {
    setList(prev => {
      const idx = prev.findIndex(d => d.id === saved.id);
      if (idx >= 0) { const next = [...prev]; next[idx] = saved; return next; }
      return [...prev, saved];
    });
    setModal(null);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`/api/distributors/${deleteTarget.id}`);
      setList(prev => prev.filter(d => d.id !== deleteTarget.id));
    } catch (err) {
      alert(err.response?.data?.message || '삭제 실패');
    }
    setDeleteTarget(null);
  };

  if (me?.superAdminYn !== 'Y') {
    return <div className="p-8 text-slate-400">접근 권한이 없습니다.</div>;
  }

  return (
    <>
      <div className="flex mb-8 justify-center items-center">
        <div className="text-xl sm:text-2xl font-bold text-white tracking-tight">유통사 관리</div>
      </div>

      {fetchError && (
        <div className="mb-4 px-4 py-3 rounded-xl bg-rose-500/10 border border-rose-500/20 text-rose-400 text-sm">
          오류: {fetchError}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-sky-500 mr-3" />
          <div className="text-sm text-slate-400">데이터 불러오는 중...</div>
        </div>
      ) : (
        <div className="space-y-3">
          {list.map(dist => (
            <div key={dist.id} className="bg-[#182334] border border-white/[0.08] rounded-2xl p-5 flex items-center gap-4">
              {/* 로고 */}
              <div className="w-12 h-12 rounded-xl bg-white/5 border border-white/[0.08] flex items-center justify-center overflow-hidden shrink-0">
                {dist.logo
                  ? <img src={dist.logo} alt={dist.name} className="w-full h-full object-contain" />
                  : <Building2 size={20} className="text-slate-500" />}
              </div>

              {/* 정보 */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-white font-semibold truncate">{dist.name}</span>
                  {dist.id === 1 && <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-violet-500/20 text-violet-400">기본</span>}
                  <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${dist.status === 'active' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-rose-500/20 text-rose-400'}`}>
                    {dist.status === 'active' ? '운영중' : '중단'}
                  </span>
                  <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${dist.theme === 'dark' ? 'bg-slate-700 text-slate-300' : 'bg-amber-500/20 text-amber-400'}`}>
                    {dist.theme === 'dark' ? '다크' : '라이트'}
                  </span>
                </div>
                <div className="flex items-center gap-3 mt-1 text-xs text-slate-500 flex-wrap">
                  <span>slug: {dist.slug}</span>
                  <span>· 권리사 {dist.clientCount || 0}명</span>
                </div>
              </div>

              {/* 액션 */}
              <div className="flex items-center gap-2 shrink-0">
                {dist.id !== 1 && (
                  <a href={`https://${dist.slug}.kdist.net`} target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400 text-xs font-semibold transition-colors">
                    <ExternalLink size={12} /> 정산시스템
                  </a>
                )}
                <button onClick={() => setModal(dist)}
                  className="p-2 rounded-lg bg-white/5 hover:bg-white/10 text-slate-400 hover:text-white transition-colors">
                  <Pencil size={15} />
                </button>
                {dist.id !== 1 && (
                  <button onClick={() => setDeleteTarget(dist)}
                    className="p-2 rounded-lg bg-rose-500/10 hover:bg-rose-500/20 text-rose-400 transition-colors">
                    <Trash2 size={15} />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="w-full flex justify-end items-center mt-6">
        <button onClick={() => setModal('create')}
          className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold bg-sky-500/20 hover:bg-sky-500/30 text-sky-400 transition-colors">
          <Plus size={16} /> 유통사 추가
        </button>
      </div>

      {/* 모달 */}
      {modal && (
        <DistributorModal
          dist={modal === 'create' ? null : modal}
          onClose={() => setModal(null)}
          onSaved={handleSaved}
        />
      )}

      {/* 삭제 확인 */}
      {deleteTarget && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-[#182334] border border-white/[0.08] rounded-2xl p-6 max-w-sm w-full shadow-2xl">
            <h3 className="text-white font-bold text-base mb-2">유통사 삭제</h3>
            <p className="text-slate-400 text-sm mb-5">
              <span className="text-white">{deleteTarget.name}</span>을 삭제하시겠습니까?<br />
              소속 권리사가 있으면 삭제되지 않습니다.
            </p>
            <div className="flex gap-2">
              <button onClick={() => setDeleteTarget(null)} className="flex-1 px-4 py-2 rounded-xl text-sm font-semibold bg-white/10 hover:bg-white/15 text-slate-300 transition-colors">취소</button>
              <button onClick={handleDelete} className="flex-1 px-4 py-2 rounded-xl text-sm font-semibold bg-rose-500/20 hover:bg-rose-500/30 text-rose-400 transition-colors">삭제</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
